import { useId } from 'react';
import type { CSSProperties, FunctionComponent } from 'react';

import { getFontSize } from '../../lib';

import styles from './FreeChip.module.css';

interface FreeChipProps {
  valueText: string;
  count?: number;
  computeStyles: CSSProperties;
}

export const FreeChip: FunctionComponent<FreeChipProps> = ({ valueText, count, computeStyles }) => {
  const prefix = useId();

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 100 100"
        className={styles.freeChip}
        style={computeStyles}
      >
        <circle cx="50" cy="50" r="50" fill={`url(#${prefix}-a)`} />
        <path
          fill={`url(#${prefix}-b)`}
          d="M89.31 50a39.28 39.28 0 1 1-78.53 0c0-21.7 17.58-39.3 39.26-39.3S89.31 28.3 89.31 50Z"
        />
        <path
          fill="#000"
          d="m89.23 51.4.01-1.04c0-21.9-17.55-39.65-39.2-39.65-21.64 0-39.19 17.75-39.19 39.65 0 .35 0 .69.02 1.03.54-21.42 17.87-38.62 39.18-38.62 21.3 0 38.64 17.2 39.18 38.62Z"
        />
        <path
          fill="#fff"
          fillOpacity=".4"
          d="M99.98 51.3A50.6 50.6 0 0 0 100 50 50 50 0 1 0 .02 51.3a50 50 0 0 1 99.96 0Z"
        />
        <path
          fill={`url(#${prefix}-c)`}
          d="M97.2 33.52a50 50 0 0 0-11.77-18.8l-4.07 4.06A44.25 44.25 0 0 1 91.78 35.4l5.43-1.9Z"
        />
        <use href={`#${prefix}-pathDataA`} fill="#fff" fillOpacity=".8" />
        <use href={`#${prefix}-pathDataA`} fill={`url(#${prefix}-c)`} />
        <use href={`#${prefix}-pathDataB`} fill="#fff" fillOpacity=".8" />
        <use href={`#${prefix}-pathDataB`} fill={`url(#${prefix}-c)`} />
        <use href={`#${prefix}-pathDataC`} fill="#fff" fillOpacity=".8" />
        <use href={`#${prefix}-pathDataC`} fill={`url(#${prefix}-c)`} />
        <use href={`#${prefix}-pathDataD`} fill="#fff" fillOpacity=".8" />
        <use href={`#${prefix}-pathDataD`} fill={`url(#${prefix}-c)`} />
        <use href={`#${prefix}-pathDataE`} fill="#fff" fillOpacity=".8" />
        <use href={`#${prefix}-pathDataE`} fill={`url(#${prefix}-c)`} />
        <foreignObject x="0" y="0" width="100" height="100">
          <div
            className={styles.freeChip__value}
            style={{ '--chipFontSize': `${getFontSize(valueText) / 10}px` } as CSSProperties}
          >
            {valueText}
          </div>
        </foreignObject>
        <defs>
          <path
            id={`${prefix}-pathDataA`}
            d="M2.8 66.48a50 50 0 0 0 11.77 18.8l4.08-4.06A44.25 44.25 0 0 1 8.23 64.6l-5.43 1.9Z"
          />
          <path
            id={`${prefix}-pathDataB`}
            d="M2.8 33.52a50 50 0 0 1 11.77-18.8l4.08 4.06A44.25 44.25 0 0 0 8.23 35.4l-5.43-1.9Z"
          />
          <path
            id={`${prefix}-pathDataC`}
            d="M97.2 66.48a50 50 0 0 1-11.77 18.8l-4.07-4.06A44.25 44.25 0 0 0 91.78 64.6l5.43 1.9Z"
          />
          <path
            id={`${prefix}-pathDataD`}
            d="M38.92 1.24a50 50 0 0 1 22.16 0L59.8 6.85a44.25 44.25 0 0 0-19.6 0l-1.28-5.6Z"
          />
          <path
            id={`${prefix}-pathDataE`}
            d="M61.08 98.76a50 50 0 0 1-22.16 0l1.28-5.61a44.25 44.25 0 0 0 19.6 0l1.28 5.6Z"
          />

          <linearGradient id={`${prefix}-a`} x1="-10" x2="94.59" y1="141" y2="9.57" gradientUnits="userSpaceOnUse">
            <stop offset=".27" stopColor="#FFBF30" />
            <stop offset=".37" stopColor="#FFA100" />
            <stop offset=".44" stopColor="#D98A00" />
            <stop offset=".58" stopColor="#FB0" />
            <stop offset=".7" stopColor="#FFF480" />
            <stop offset=".76" stopColor="#FEB321" />
            <stop offset=".93" stopColor="#FFA806" />
          </linearGradient>
          <linearGradient id={`${prefix}-b`} x1="50.28" x2="49.74" y1="2.68" y2="110.94" gradientUnits="userSpaceOnUse">
            <stop stopColor="#422F00" />
            <stop offset=".35" stopColor="#2E2000" />
            <stop offset="1" />
          </linearGradient>
          <linearGradient id={`${prefix}-c`} x1="26.4" x2="81.23" y1="4.5" y2="88.68" gradientUnits="userSpaceOnUse">
            <stop stopColor="#372601" />
            <stop offset="1" stopColor="#040301" />
          </linearGradient>
        </defs>
      </svg>
      {Number.isInteger(count) && (
        <div className={styles.freeChip__badgeContainer} id="counterText">
          <div className={styles.freeChip__badge}>
            <span className={styles.freeChip__badgeText}>{count}</span>
          </div>
        </div>
      )}
    </>
  );
};
